/**
 * Insights Container Component
 *
 * Main container for the Startup Insights chat interface.
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  Pressable,
} from 'react-native';
import type { LayoutChangeEvent } from 'react-native';
import Animated, {
  FadeIn,
  SlideInRight,
  useAnimatedStyle,
  withSpring,
} from 'react-native-reanimated';
import * as Haptics from 'expo-haptics';
import {
  X,
  Maximize2,
  Minimize2,
  MoreHorizontal,
  Lightbulb,
  ChevronRight,
  CheckCircle,
  Trash2,
  Bookmark,
  BookmarkCheck,
} from 'lucide-react-native';
import { getUserMessage } from '@/lib/errors';
import { useStartupInsights, insightsActionItemToRecommendation } from '@/lib/startup-insights';
import { useScoreStore } from '@/lib/state/score-store';
import { useAiConsentGate } from '@/lib/consent/ai-consent';
import type { InsightsContainerProps, InsightsQuickAction } from '@/lib/startup-insights/types';
import { INSIGHTS_QUICK_ACTIONS } from '@/lib/startup-insights/types';
import { InsightsMessage } from './InsightsMessage';
import { InsightsInput } from './InsightsInput';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useThemeColors } from '@/lib/useThemeColors';
import { AppModal, ModalAction } from '@/components/modal';

// The collapsed-state height. Kept as a fixed number — collapse is a
// deliberate "make it smaller" toggle, not a fill target — but still clamped
// against a measured `availableHeight` above so it can never ask for more
// room than the host actually gave the panel.
const COMPACT_HEIGHT = 400;

export function InsightsContainer({
  startupContext,
  startupId,
  initialCompact = false,
  presentation = 'panel',
  onClose,
  onActionItemsChange,
}: InsightsContainerProps) {
  const colors = useThemeColors();
  const [inputValue, setInputValue] = useState('');
  const [isExpanded, setIsExpanded] = useState(!initialCompact);
  const [showActionItems, setShowActionItems] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const scrollRef = useRef<ScrollView>(null);

  // The space the host actually gives this panel (src/app/(startup)/insights.tsx
  // hands it a `flex-1` slot below a header and a score banner). `null` until
  // the first layout pass — used to avoid springing toward a guess before one
  // exists. Re-measures automatically: KeyboardAvoidingView shrinks this same
  // slot when the keyboard opens, and onLayout fires again with the new size.
  const [availableHeight, setAvailableHeight] = useState<number | null>(null);
  const handleMeasureAvailableHeight = useCallback((event: LayoutChangeEvent) => {
    const measured = event.nativeEvent.layout.height;
    setAvailableHeight((prev) => (prev === measured ? prev : measured));
  }, []);

  // Initialize insights hook
  const {
    messages,
    isLoading,
    error,
    sendMessage,
    clearHistory,
    suggestedQuestions,
    actionItems,
    sessionTokens,
  } = useStartupInsights({
    startupContext,
    onActionItems: onActionItemsChange,
  });

  // AI-processing consent gate — shown before the FIRST message of a
  // session, at every entry point that can send one.
  const { runWithConsent, openConsentSheet, sheet: consentSheet } = useAiConsentGate(['startup_insights']);
  const isConsentRequiredError = error?.message === 'Consent required';

  // 403 recovery: a race, or consent withdrawn from another device/session —
  // the cached "consented" flag said yes, the server said no. Reopen the
  // sheet rather than leaving the founder at a dead-end error bubble.
  useEffect(() => {
    if (isConsentRequiredError) openConsentSheet();
  }, [isConsentRequiredError, openConsentSheet]);

  // Handle send
  const handleSend = useCallback(() => {
    if (inputValue.trim()) {
      const content = inputValue.trim();
      setInputValue('');
      runWithConsent(() => sendMessage(content));
    }
  }, [inputValue, sendMessage, runWithConsent]);

  // Handle quick action
  const handleQuickAction = useCallback(
    (action: InsightsQuickAction) => {
      runWithConsent(() => sendMessage(action.prompt, action.focusArea));
    },
    [sendMessage, runWithConsent]
  );

  // Handle suggestion tap
  const handleSuggestionTap = useCallback(
    (suggestion: string) => {
      runWithConsent(() => sendMessage(suggestion));
    },
    [sendMessage, runWithConsent]
  );

  // Auto-scroll on new messages
  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(() => {
        scrollRef.current?.scrollToEnd({ animated: true });
      }, 200);
    }
  }, [messages.length]);

  // Animated expand style — measured, not computed. The panel's only host
  // gives it a flex-1 slot; a hardcoded expanded height could not fill a tall
  // device's slot, leaving dead space between the composer and the tab bar.
  // The expanded target tracks `availableHeight` (from `handleMeasureAvailableHeight`
  // below) instead of a magic number. The collapsed target stays a fixed,
  // smaller height — clamped to whatever room actually exists — so the
  // Maximize2/Minimize2 toggle keeps meaning something. Before the first
  // layout pass `availableHeight` is null: render the target directly, with
  // no spring, so there is nothing to visibly jump away from once a real
  // measurement arrives.
  // Captured as plain strings: a worklet should close over primitives, not
  // walk a theme object on the UI thread.
  const surfaceColor = colors.surface.primary;
  const borderColor = colors.border.light;

  const containerStyle = useAnimatedStyle(() => {
    const expandedTarget = availableHeight ?? COMPACT_HEIGHT;
    const compactTarget = availableHeight === null ? COMPACT_HEIGHT : Math.min(COMPACT_HEIGHT, availableHeight);
    // A full page is always at its expanded target: there is no window to
    // minimise, so `isExpanded` simply does not apply to that presentation.
    const target = isExpanded || presentation === 'page' ? expandedTarget : compactTarget;

    return {
      height: availableHeight === null
        ? target
        : withSpring(target, { damping: 20, stiffness: 150 }),
      // These are static, but they MUST be returned from the worklet rather
      // than passed alongside `containerStyle` in a style array: an
      // Animated.View drops the static entry of such an array (measured in
      // CLAUDE.md <pressable_sizing>), so the panel would have rendered with
      // no background and no border while every style object looked correct.
      backgroundColor: surfaceColor,
      borderWidth: 1,
      borderColor: borderColor,
    };
  }, [isExpanded, availableHeight, presentation, surfaceColor, borderColor]);

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      className="flex-1"
    >
      {/* Plain (non-animated) measurer — NOT an Animated.View, so this is not
          the array-style trap CLAUDE.md documents (an animated node loses a
          static layout entry handed to it in a style array). This wrapper's
          own layout comes from `flex-1` alone; it reports the slot's real
          height to `containerStyle` above without depending on the animated
          child's current (in-flight) height. */}
      <View
        className="flex-1"
        onLayout={handleMeasureAvailableHeight}
        testID="insights-panel-measurer"
      >
        <Animated.View
          entering={SlideInRight.duration(300)}
          style={containerStyle}
          className="rounded-2xl overflow-hidden"
        >
        {/* Conversation header. On a full PAGE this is the advisor's identity
            and its contextual actions only — the page frame above already owns
            the title and the way out, and a page does not minimise itself. In
            an embedded PANEL the window controls remain, because there the
            panel really is a window inside a host screen. */}
        <View
          className="flex-row items-center justify-between px-4 py-3"
          style={{ backgroundColor: colors.surface.tertiary, borderBottomWidth: 1, borderBottomColor: colors.border.light }}
        >
          <View className="flex-row items-center gap-2 flex-1">
            <View
              className="w-8 h-8 rounded-full items-center justify-center shrink-0"
              style={{ backgroundColor: colors.amber.light }}
            >
              <Lightbulb size={16} color={colors.amber.main} />
            </View>
            {/* flex-1 + numberOfLines: a long company name truncates instead of
                pushing the controls past the edge. */}
            <View className="flex-1">
              <Text className="text-sm font-medium" numberOfLines={1} style={{ color: colors.text.primary }}>
                AI Advisor
              </Text>
              <Text className="text-xs" numberOfLines={1} style={{ color: colors.text.tertiary }}>
                Improve {startupContext.name}
              </Text>
            </View>
          </View>

          {/* shrink-0: the essential controls keep their room whatever the
              company name does. Every one is a 44pt target — they were 32px. */}
          <View className="flex-row items-center gap-1 shrink-0">
            {/* Saved action items — a labelled toggle, not a bare count. */}
            {actionItems.length > 0 && (
              <AnimatedPressable
                onPress={() => setShowActionItems(!showActionItems)}
                accessibilityRole="button"
                accessibilityLabel={`Saved action items, ${actionItems.length}`}
                accessibilityState={{ selected: showActionItems }}
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 22,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: showActionItems ? colors.amber.light : colors.surface.primary,
                }}
              >
                <CheckCircle size={18} color={showActionItems ? colors.amber.main : colors.text.secondary} />
              </AnimatedPressable>
            )}

            {/* Contextual page actions. THIS is what three dots is for: actions
                on the thing in front of you. It is never a destination list. */}
            {(messages.length > 0 || presentation === 'panel') && (
              <AnimatedPressable
                onPress={() => setShowMenu(true)}
                accessibilityRole="button"
                accessibilityLabel="Conversation actions"
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 22,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: colors.surface.primary,
                }}
              >
                <MoreHorizontal size={18} color={colors.text.secondary} />
              </AnimatedPressable>
            )}

            {/* Window controls belong to an embedded panel only. A full page is
                left through the page header, and cannot meaningfully minimise. */}
            {presentation === 'panel' && (
              <>
                <AnimatedPressable
                  onPress={() => setIsExpanded(!isExpanded)}
                  accessibilityRole="button"
                  accessibilityLabel={isExpanded ? 'Minimise the advisor' : 'Expand the advisor'}
                  style={{
                    width: 44, height: 44, borderRadius: 22,
                    alignItems: 'center', justifyContent: 'center',
                    backgroundColor: colors.surface.primary,
                  }}
                >
                  {isExpanded ? (
                    <Minimize2 size={18} color={colors.text.secondary} />
                  ) : (
                    <Maximize2 size={18} color={colors.text.secondary} />
                  )}
                </AnimatedPressable>
                {onClose && (
                  <AnimatedPressable
                    onPress={onClose}
                    accessibilityRole="button"
                    accessibilityLabel="Close the advisor"
                    style={{
                      width: 44, height: 44, borderRadius: 22,
                      alignItems: 'center', justifyContent: 'center',
                      backgroundColor: colors.surface.primary,
                    }}
                  >
                    <X size={18} color={colors.text.secondary} />
                  </AnimatedPressable>
                )}
              </>
            )}
          </View>
        </View>

        {/* Session metadata lives in the BODY, not in the navigation row. It
            grows during use — token count, saved count — and a row that has to
            hold both a company name and a growing counter is the row that
            squeezes the exit control out. */}
        {sessionTokens > 0 && (
          <View className="px-4 pt-2">
            <Text className="text-[10px]" numberOfLines={1} style={{ color: colors.text.tertiary }}>
              {(sessionTokens / 1000).toFixed(1)}k tokens used this session
            </Text>
          </View>
        )}

        {/* Content area */}
        <View className="flex-1">
          {showActionItems ? (
            <ActionItemsView
              items={actionItems}
              onClose={() => setShowActionItems(false)}
            />
          ) : messages.length === 0 ? (
            <WelcomeView
              startupName={startupContext.name}
              score={startupContext.scores?.finalScore}
              onQuickAction={handleQuickAction}
            />
          ) : (
            <ScrollView
              ref={scrollRef}
              className="flex-1 px-4 py-4"
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
            >
              {messages.map((msg, index) => (
                <InsightsMessage
                  key={msg.id}
                  message={msg}
                  isLast={index === messages.length - 1}
                  isStreaming={isLoading && index === messages.length - 1}
                  actionItems={msg.metadata?.actionItems}
                />
              ))}

              {/* Loading indicator */}
              {isLoading && messages[messages.length - 1]?.role === 'user' && (
                <LoadingIndicator />
              )}

              {/* Suggested questions */}
              {!isLoading && suggestedQuestions.length > 0 && (
                <SuggestedQuestions
                  questions={suggestedQuestions}
                  onTap={handleSuggestionTap}
                />
              )}

              {/* Error message — the consent-required case shows the
                  consent sheet instead (see the effect above), not a
                  dead-end error bubble. */}
              {error && !isConsentRequiredError && <ErrorMessage error={error} />}

              {/* Spacer for scroll */}
              <View className="h-4" />
            </ScrollView>
          )}
        </View>

        {/* Input */}
        {!showActionItems && (
          <InsightsInput
            value={inputValue}
            onChangeText={setInputValue}
            onSubmit={handleSend}
            disabled={isLoading}
            placeholder={
              messages.length === 0
                ? 'Ask how to improve your score...'
                : 'Follow-up question...'
            }
          />
        )}
        </Animated.View>
      </View>

      {/* Reset moved OUT of the navigation row and into this labelled menu —
          it kept its function, it stopped competing for the row that has to
          hold the company name. Centered card, per the modal rules. */}
      <AppModal
        visible={showMenu}
        onClose={() => setShowMenu(false)}
        title="Conversation"
        footer={
          <AppModal.Actions>
            <ModalAction label="Close" variant="neutral" onPress={() => setShowMenu(false)} />
            <ModalAction
              label="Reset conversation"
              variant="destructive"
              disabled={messages.length === 0}
              onPress={() => {
                setShowMenu(false);
                clearHistory();
              }}
            />
          </AppModal.Actions>
        }
      >
        <Text className="text-base" style={{ color: colors.text.secondary }}>
          This conversation is session-only and is never shared with investors.
          Resetting clears it from this device.
        </Text>
      </AppModal>

      {consentSheet}
    </KeyboardAvoidingView>
  );
}

/**
 * Welcome view with quick actions and score context
 */
function WelcomeView({
  startupName,
  score,
  onQuickAction,
}: {
  startupName: string;
  score?: number;
  onQuickAction: (action: InsightsQuickAction) => void;
}) {
  const colors = useThemeColors();
  return (
    <ScrollView
      className="flex-1 px-4 py-4"
      showsVerticalScrollIndicator={false}
    >
      {/* Welcome message */}
      <Animated.View
        entering={FadeIn.delay(100)}
        className="rounded-xl p-4 mb-4"
        style={{ backgroundColor: colors.surface.tertiary }}
      >
        <Text className="text-base font-medium mb-2" style={{ color: colors.text.primary }}>
          AI Advisor
        </Text>
        <Text className="text-sm leading-relaxed" style={{ color: colors.text.secondary }}>
          I'll help you improve {startupName}'s investment readiness
          {score !== undefined && ` (current score: ${score}/100)`}.
          Ask me about improving your score, preparing for investors, or
          identifying gaps.
        </Text>
      </Animated.View>

      {/* Score context if available */}
      {score !== undefined && (
        <Animated.View
          entering={FadeIn.delay(150)}
          className="mb-4"
        >
          <View
            className="p-3 rounded-xl border"
            style={{
              backgroundColor: colors.surface.tertiary,
              borderColor: score >= 70 ? colors.traffic.green : score >= 50 ? colors.amber.main : colors.traffic.red,
            }}
          >
            <View className="flex-row items-center justify-between">
              <Text className="text-sm" numberOfLines={1} style={{ color: colors.text.secondary }}>Current Score</Text>
              <Text
                className="text-2xl font-bold"
                style={{ color: score >= 70 ? colors.traffic.green : score >= 50 ? colors.amber.main : colors.traffic.red }}
              >
                {score}/100
              </Text>
            </View>
            <Text className="text-xs mt-1" style={{ color: colors.text.tertiary }}>
              {score >= 70 ? 'Investor-ready' :
               score >= 50 ? 'Developing - room for improvement' :
               'Early stage - focus on fundamentals'}
            </Text>
          </View>
        </Animated.View>
      )}

      {/* Quick actions */}
      <Animated.View entering={FadeIn.delay(200)}>
        <Text className="text-xs font-medium uppercase mb-3" style={{ color: colors.text.tertiary }}>
          Get Started
        </Text>
        <View className="gap-2">
          {INSIGHTS_QUICK_ACTIONS.slice(0, 4).map((action, index) => (
            <Animated.View
              key={action.id}
              entering={FadeIn.delay(250 + index * 50)}
            >
              <AnimatedPressable
                onPress={() => onQuickAction(action)}
                className="flex-row items-center justify-between px-4 py-3 rounded-xl border"
                style={{ backgroundColor: colors.surface.tertiary, borderColor: colors.border.light }}
              >
                <View className="flex-1 mr-3">
                  <Text className="text-sm font-medium" numberOfLines={1} style={{ color: colors.text.primary }}>
                    {action.label}
                  </Text>
                  <Text className="text-xs mt-0.5" numberOfLines={2} style={{ color: colors.text.tertiary }}>
                    {action.description}
                  </Text>
                </View>
                <ChevronRight size={16} color={colors.text.tertiary} />
              </AnimatedPressable>
            </Animated.View>
          ))}
        </View>
      </Animated.View>

      {/* Privacy note */}
      <Animated.View entering={FadeIn.delay(500)} className="mt-6">
        <Text className="text-xs text-center" style={{ color: colors.text.tertiary }}>
          Session-only • Private coaching • Not shared with investors
        </Text>
      </Animated.View>
    </ScrollView>
  );
}

/**
 * Action items view
 */
function ActionItemsView({
  items,
  onClose,
}: {
  items: Array<{ id: string; action: string; priority: 'high' | 'medium' | 'low' }>;
  onClose: () => void;
}) {
  const colors = useThemeColors();
  const aiRecommendations = useScoreStore((s) => s.aiRecommendations);
  const addAIRecommendation = useScoreStore((s) => s.addAIRecommendation);

  const savedIds = new Set(
    aiRecommendations
      .filter((r) => r.source === 'insights')
      .map((r) => r.id)
  );

  const handleSave = (item: { id: string; action: string; priority: 'high' | 'medium' | 'low' }) => {
    const rec = insightsActionItemToRecommendation(item);
    if (!savedIds.has(rec.id)) {
      addAIRecommendation(rec);
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    }
  };

  return (
    <View className="flex-1 px-4 py-4">
      <View className="flex-row items-center justify-between mb-4">
        <Text className="text-sm font-medium flex-1" numberOfLines={1} style={{ color: colors.text.primary }}>
          Action Items ({items.length})
        </Text>
        <View style={{ height: 44, justifyContent: 'center' }} className="shrink-0">
          <Pressable onPress={onClose} accessibilityRole="button" accessibilityLabel="Back to the conversation">
            <Text className="text-sm font-medium" style={{ color: colors.primary[600] }}>Back to Chat</Text>
          </Pressable>
        </View>
      </View>

      <ScrollView showsVerticalScrollIndicator={false}>
        {items.map((item, index) => {
          const isSaved = savedIds.has(`insights_${item.id}`);
          return (
            <View
              key={item.id}
              className="p-3 rounded-xl mb-2 border-l-4"
              style={{
                backgroundColor: colors.surface.tertiary,
                borderLeftColor:
                  item.priority === 'high' ? colors.traffic.red :
                  item.priority === 'medium' ? colors.amber.main :
                  colors.border.medium,
              }}
            >
              <View className="flex-row items-start gap-3">
                <View
                  className="w-6 h-6 rounded-full items-center justify-center shrink-0"
                  style={{ backgroundColor: colors.surface.primary }}
                >
                  <Text className="text-xs" style={{ color: colors.text.secondary }}>{index + 1}</Text>
                </View>
                <View className="flex-1">
                  <Text className="text-sm" style={{ color: colors.text.primary }}>{item.action}</Text>
                  <Text
                    className="text-xs mt-1 capitalize"
                    style={{
                      color:
                        item.priority === 'high' ? colors.traffic.red :
                        item.priority === 'medium' ? colors.amber.main :
                        colors.text.tertiary,
                    }}
                  >
                    {item.priority} priority
                  </Text>
                </View>
                <Pressable
                  onPress={() => !isSaved && handleSave(item)}
                  accessibilityRole="button"
                  accessibilityLabel={isSaved ? 'Saved to recommendations' : 'Save to recommendations'}
                  className="items-center justify-center rounded-lg shrink-0"
                  style={{ width: 44, height: 44, backgroundColor: isSaved ? colors.amber.light : colors.surface.primary }}
                >
                  {isSaved ? (
                    <BookmarkCheck size={18} color={colors.amber.main} />
                  ) : (
                    <Bookmark size={18} color={colors.text.secondary} />
                  )}
                </Pressable>
              </View>
            </View>
          );
        })}
      </ScrollView>
    </View>
  );
}

/**
 * Loading indicator
 */
function LoadingIndicator() {
  const colors = useThemeColors();
  return (
    <Animated.View
      entering={FadeIn}
      className="flex-row items-center gap-3 px-4 py-3 rounded-xl mb-4"
      style={{ backgroundColor: colors.surface.tertiary }}
    >
      <View
        className="w-6 h-6 rounded-full items-center justify-center"
        style={{ backgroundColor: colors.amber.light }}
      >
        <Lightbulb size={12} color={colors.amber.main} />
      </View>
      <View className="flex-row gap-1">
        <View className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: colors.amber.main }} />
        <View className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: colors.amber.main }} />
        <View className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: colors.amber.main }} />
      </View>
      <Text className="text-sm" style={{ color: colors.text.secondary }}>Thinking...</Text>
    </Animated.View>
  );
}

/**
 * Suggested questions
 */
function SuggestedQuestions({
  questions,
  onTap,
}: {
  questions: string[];
  onTap: (question: string) => void;
}) {
  const colors = useThemeColors();
  return (
    <Animated.View entering={FadeIn} className="mt-2 mb-2">
      <Text className="text-xs mb-2" style={{ color: colors.text.tertiary }}>Suggested:</Text>
      <View className="gap-2">
        {questions.slice(0, 3).map((question, index) => (
          <AnimatedPressable
            key={index}
            onPress={() => onTap(question)}
            className="px-3 py-2 rounded-lg border"
            style={{ backgroundColor: colors.surface.tertiary, borderColor: colors.border.light }}
          >
            <Text className="text-sm" style={{ color: colors.text.secondary }}>{question}</Text>
          </AnimatedPressable>
        ))}
      </View>
    </Animated.View>
  );
}

/**
 * Error message
 */
function ErrorMessage({ error }: { error: Error }) {
  const colors = useThemeColors();
  return (
    <Animated.View
      entering={FadeIn}
      className="px-4 py-3 rounded-xl border mb-4"
      style={{ backgroundColor: colors.red.light, borderColor: colors.red.main }}
    >
      <Text className="text-sm" style={{ color: colors.red.main }}>
        {getUserMessage(error)}
      </Text>
    </Animated.View>
  );
}
